import React, { useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Heading1 } from '../components/atoms/Typography';
import { SettingsWizardProvider } from '../context/SettingsWizardContext';
import { SETTINGS_STEPS } from '../data/settingsSteps';
import CategoryTypeStep from '../components/organisms/settings-steps/CategoryTypeStep';
// import DepartmentsStep from '../components/organisms/settings-steps/DepartmentsStep';
// import PrioritiesStep from '../components/organisms/settings-steps/PrioritiesStep';
import WorkflowStep from '../components/organisms/settings-steps/WorkflowStep';
import FormFieldsStep from '../components/organisms/settings-steps/FormFieldsStep';
import ReviewPublishStep from '../components/organisms/settings-steps/ReviewPublishStep';
import { useDispatch } from 'react-redux';
import { setId } from '../redux/appDetails/reducer';
function clampStep(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 1;
  if (n > SETTINGS_STEPS.length) return SETTINGS_STEPS.length;
  return Math.floor(n);
}

function clampSubstep(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 1;
  if (n > 3) return 3;
  return Math.floor(n);
}

/**
 * Complaint Management Settings wizard.
 * Step navigation is driven by URL search params: ?step=2&substep=1
 */
export default function ComplaintSettingsPage({
  title = 'Complaint Management Settings',
  onPublish,
  onStepChange,
  onSaveDraft,
  roleList = [],
  userList = [],
  storeList,
  showSaveButton = true,
  showNextButton = true,
 
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { systemId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const step = clampStep(searchParams.get('step') || 1);
  const substep = clampSubstep(searchParams.get('substep') || 1);

  // const [systemIds, setSystemIds] = React.useState(systemId);


  useEffect(() => {
    if (systemId) {
      dispatch(setId(systemId));
    }
  }, [dispatch, systemId]);



  // Keep URL in sync when step param is missing (e.g. /settings/qms → ?step=1)
  useEffect(() => {
    if (!searchParams.get('step')) {
      const params = new URLSearchParams(searchParams);
      params.set('step', '1');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const stepMeta = useMemo(
    () => SETTINGS_STEPS.find((s) => s.id === step) || SETTINGS_STEPS[0],
    [step],
  );

  const setStep = useCallback(
    (nextStep, nextSubstep) => {
      const params = new URLSearchParams(searchParams);
      params.set('step', String(nextStep));
      if (nextStep === 4) {
        params.set('substep', String(nextSubstep ?? (nextStep === step ? substep : 1)));
      } else {
        params.delete('substep');
      }
      setSearchParams(params, { replace: false });
      onStepChange?.({ systemId, step: nextStep, substep: nextSubstep });
    },
    [searchParams, setSearchParams, onStepChange, systemId, step, substep],
  );

  const goNext = () => {
    if (step < SETTINGS_STEPS.length) {
      setStep(step + 1, 1);
      return;
    }
    navigate('/');
  };

  const handleSubstepChange = (next) => {
    setStep(4, next);
  };

  const handlePublish = () => {
    onPublish?.({ systemId, step });
  };

  const stepperProps = {
    currentStep: step,
    onStepClick: (id) => setStep(id, id === 4 ? 1 : undefined),
    showSaveButton,
    showNextButton,
    onSaveDraft: () => onSaveDraft?.({ systemId, step }),
    onNext: goNext,
    className: 'ca-settings-page__stepper',
  };

  return (
    <SettingsWizardProvider key={systemId || 'new'}>
      <div className="ca-settings-page">
        <div className="ca-settings-page__header">
          <button
            type="button"
            className="ca-settings-page__back"
            onClick={() => navigate('/')}
            aria-label="Back to Complaint Management"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M15 6l-6 6 6 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <Heading1>{title}</Heading1>
        </div>

        {step === 1 ? (
          <CategoryTypeStep stepMeta={stepMeta} stepperProps={stepperProps}   roleList={roleList}/>
        ) : null}
        {step === 2 ? (
            <WorkflowStep
            onNext={goNext}
            stepMeta={stepMeta}
            roleList={roleList}
            userList={userList}
           
            substep={substep}
            onSubstepChange={handleSubstepChange}
            stepperProps={stepperProps}
          />
        ) : null}
        {/* {step === 5 ? (
          <PrioritiesStep stepMeta={stepMeta} stepperProps={stepperProps} />
        ) : null} */}
       
        {step === 3 ? (
          <FormFieldsStep stepMeta={stepMeta} stepperProps={stepperProps}  />
        ) : null}
        {step === 4 ? (
          <ReviewPublishStep storeList={storeList} onPublish={handlePublish} stepMeta={stepMeta} stepperProps={stepperProps} />
        ) : null}
      </div>
    </SettingsWizardProvider>
  );
}
